import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sparkles,
  Loader2,
  Send,
  MessageSquare,
  X,
  Check,
  CheckCircle2,
  Clock,
  Palette,
  Zap,
  Video,
  Music,
  Scissors,
  Target,
  Upload,
  FileText,
  Play,
  Pause,
  GripVertical,
  Trash2,
  Volume2,
  Captions,
  SlidersHorizontal,
} from 'lucide-react';
import type { ProjectFile } from '@/types/video-enhancer';
import { resolveVideoUrl } from '@/lib/utils';

interface PlanMessage {
  role: 'ai' | 'user';
  content: string;
  timestamp: number;
}

interface PlanQuestion {
  id: string;
  question: string;
  options: string[];
}

interface ClipPriority {
  assetName: string;
  priority: 'primary' | 'secondary' | 'b-roll';
  reason: string;
  suggestedTrim?: string;
}

interface ModifiedClip extends ClipPriority {
  inTime?: number;
  outTime?: number;
}

interface FinishingTouches {
  enhanceAudio?: boolean;
  addCaptions?: boolean;
  captionStyle?: 'bottom-center' | 'dynamic-word' | 'karaoke';
  filter?: string;
  removeBackground?: boolean;
  backgroundUrl?: string;
}

/** Parse a time string like "2.5s", "2:30", or "25" into seconds */
function parseTimeValue(s: string): number | null {
  s = s.trim().replace(/s$/i, '');
  // M:SS format
  const mmss = s.match(/^(\d+):(\d{1,2}(?:\.\d+)?)$/);
  if (mmss) {
    return parseInt(mmss[1], 10) * 60 + parseFloat(mmss[2]);
  }
  // Plain seconds
  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

/** Parse suggestedTrim string like "0.5s-3.2s" into { inTime, outTime } */
function parseSuggestedTrim(trim: string): { inTime: number; outTime: number } | null {
  if (!trim) return null;
  // Split on " - ", "-", " to "
  const parts = trim.split(/\s*(?:-|to)\s*/i);
  if (parts.length < 2) return null;
  const inVal = parseTimeValue(parts[0]);
  const outRaw = parts[1].trim().toLowerCase();
  if (outRaw === 'end') {
    // Can't resolve without duration — caller handles this
    return inVal != null ? { inTime: inVal, outTime: -1 } : null;
  }
  const outVal = parseTimeValue(parts[1]);
  if (inVal == null || outVal == null) return null;
  return { inTime: inVal, outTime: outVal };
}

/** Format seconds to display string with 1 decimal */
function formatTime(seconds: number): string {
  return seconds.toFixed(1);
}

interface EditPlan {
  summary: string;
  tone: string;
  style: string;
  pacing: 'fast' | 'medium' | 'slow';
  keyMoments: string[];
  suggestedDuration: number;
  focusAreas: string[];
  musicDirection?: string;
  transitionStyle?: string;
  colorGrade?: string;
  textOverlays?: string[];
  clipPriorities?: ClipPriority[];
  finishingTouches?: FinishingTouches;
}

interface PlanModeChatProps {
  conversation: PlanMessage[];
  questions: PlanQuestion[];
  editPlan: EditPlan | null;
  isProcessing: boolean;
  isPlanComplete: boolean;
  isRendering?: boolean;
  scriptContent?: string | null;
  scriptFileName?: string | null;
  finishingTouches: FinishingTouches;
  files: ProjectFile[];
  aspectRatio: string;
  onAspectRatioChange: (ratio: string) => void;
  onSendMessage: (message: string) => void;
  onQuestionSelect: (questionId: string, option: string) => void;
  onApproveAndRender: (modifiedClips?: ClipPriority[]) => void;
  onRefinePlan: () => void;
  onScriptUpload?: (content: string, fileName: string) => void;
  onFinishingTouchesChange: (touches: FinishingTouches) => void;
  onClose: () => void;
}

const FILTER_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'boost', label: 'Boost' },
  { value: 'contrast', label: 'High Contrast' },
  { value: 'muted', label: 'Muted' },
  { value: 'darken', label: 'Darken' },
  { value: 'lighten', label: 'Lighten' },
  { value: 'greyscale', label: 'Greyscale' },
  { value: 'negative', label: 'Negative' },
];

const CAPTION_STYLE_OPTIONS = [
  { value: 'bottom-center', label: 'Bottom Center' },
  { value: 'dynamic-word', label: 'Dynamic Word' },
  { value: 'karaoke', label: 'Karaoke' },
];

export const PlanModeChat: React.FC<PlanModeChatProps> = ({
  conversation,
  questions,
  editPlan,
  isProcessing,
  isPlanComplete,
  isRendering = false,
  scriptContent,
  scriptFileName,
  finishingTouches,
  files,
  aspectRatio,
  onAspectRatioChange,
  onSendMessage,
  onQuestionSelect,
  onApproveAndRender,
  onRefinePlan,
  onScriptUpload,
  onFinishingTouchesChange,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [modifiedClips, setModifiedClips] = useState<ModifiedClip[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Set<string>>>({});
  const [previewClipIndex, setPreviewClipIndex] = useState<number | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialFileInputRef = useRef<HTMLInputElement>(null);
  const inlineFileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Build a lookup map: assetName → ProjectFile
  const filesByName = React.useMemo(() => {
    const map = new Map<string, ProjectFile>();
    for (const f of files) {
      map.set(f.name, f);
    }
    return map;
  }, [files]);

  // Initialize modified clips with parsed trim values when editPlan changes
  useEffect(() => {
    if (editPlan?.clipPriorities) {
      setModifiedClips(editPlan.clipPriorities.map(clip => {
        const parsed = clip.suggestedTrim ? parseSuggestedTrim(clip.suggestedTrim) : null;
        const file = filesByName.get(clip.assetName);
        let inTime = parsed?.inTime;
        let outTime = parsed?.outTime;
        // Handle "end" marker
        if (outTime === -1 && file) {
          outTime = file.duration || undefined;
        }
        return { ...clip, inTime, outTime };
      }));
      setPreviewClipIndex(null);
    }
  }, [editPlan, filesByName]);

  useEffect(() => {
    // Scroll to bottom whenever conversation or questions change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, questions, editPlan]);

  const handleSend = () => {
    if (inputValue.trim() && !isProcessing) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleRemoveClip = (index: number) => {
    setModifiedClips(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newClips = [...modifiedClips];
    const draggedClip = newClips[draggedIndex];
    newClips.splice(draggedIndex, 1);
    newClips.splice(index, 0, draggedClip);

    setModifiedClips(newClips);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleTrimChange = (index: number, field: 'inTime' | 'outTime', value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setModifiedClips(prev => prev.map((clip, i) => {
      if (i !== index) return clip;
      const updated = { ...clip, [field]: num };
      // Regenerate suggestedTrim string
      const inStr = formatTime(updated.inTime ?? 0);
      const outStr = updated.outTime != null ? formatTime(updated.outTime) : 'end';
      updated.suggestedTrim = `${inStr}s-${outStr}${updated.outTime != null ? 's' : ''}`;
      return updated;
    }));
  };

  const getClipVideoUrl = useCallback((clip: ModifiedClip): string | null => {
    const file = filesByName.get(clip.assetName);
    if (!file) return null;
    return resolveVideoUrl(file.s3Url, file.s3Key);
  }, [filesByName]);

  const handlePreviewClip = (index: number) => {
    if (previewClipIndex === index) {
      // Toggle off
      setPreviewClipIndex(null);
      setIsVideoPlaying(false);
      return;
    }
    setPreviewClipIndex(index);
    setIsVideoPlaying(false);
  };

  const handleVideoLoaded = () => {
    const video = videoRef.current;
    if (!video || previewClipIndex === null) return;
    const clip = modifiedClips[previewClipIndex];
    if (clip?.inTime != null) {
      video.currentTime = clip.inTime;
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || previewClipIndex === null) return;
    const clip = modifiedClips[previewClipIndex];
    if (clip?.outTime != null && video.currentTime >= clip.outTime) {
      video.pause();
      setIsVideoPlaying(false);
      // Reset to inTime
      video.currentTime = clip.inTime ?? 0;
    }
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const clip = previewClipIndex !== null ? modifiedClips[previewClipIndex] : null;
      if (clip?.inTime != null && video.currentTime < clip.inTime) {
        video.currentTime = clip.inTime;
      }
      video.play();
      setIsVideoPlaying(true);
    } else {
      video.pause();
      setIsVideoPlaying(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onScriptUpload) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        onScriptUpload(content, file.name);
      };
      reader.readAsText(file);
    }
    if (initialFileInputRef.current) initialFileInputRef.current.value = '';
    if (inlineFileInputRef.current) inlineFileInputRef.current.value = '';
  };

  const getPacingIcon = (pacing: string) => {
    switch (pacing) {
      case 'fast': return <Zap className="h-3 w-3" />;
      case 'slow': return <Clock className="h-3 w-3" />;
      default: return <Video className="h-3 w-3" />;
    }
  };

  return (
    <Card className="flex h-full max-h-[calc(100vh-200px)] flex-col border-border bg-[hsl(var(--panel))]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border p-3">
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          AI Creative Editor
          {isPlanComplete && <Check className="h-3.5 w-3.5 text-success" />}
        </h3>
        {previewClipIndex !== null && (
          <button
            onClick={() => { setPreviewClipIndex(null); setIsVideoPlaying(false); }}
            className="mr-2 text-xs text-muted-foreground hover:text-foreground"
          >
            Close Preview
          </button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Script Indicator */}
      {scriptContent && (
        <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-border bg-secondary/30 p-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">
            Script loaded: {scriptFileName || 'Untitled'}
          </span>
        </div>
      )}

      {/* Video Preview Player — fixed above scroll area */}
      {previewClipIndex !== null && (() => {
        const previewClip = modifiedClips[previewClipIndex];
        const videoUrl = previewClip ? getClipVideoUrl(previewClip) : null;
        if (!previewClip || !videoUrl) return null;
        return (
          <div className="border-b border-border bg-black">
            <video
              ref={videoRef}
              src={videoUrl}
              onLoadedMetadata={handleVideoLoaded}
              onTimeUpdate={handleVideoTimeUpdate}
              onEnded={() => setIsVideoPlaying(false)}
              onPause={() => setIsVideoPlaying(false)}
              onPlay={() => setIsVideoPlaying(true)}
              className="w-full max-h-[280px] object-contain"
              playsInline
              controls
            />
            <div className="flex items-center gap-3 bg-secondary/50 px-3 py-1.5">
              <button
                onClick={handlePlayPause}
                className="text-foreground hover:text-primary transition-colors"
              >
                {isVideoPlaying
                  ? <Pause className="h-5 w-5" />
                  : <Play className="h-5 w-5" />
                }
              </button>
              <span className="flex-1 truncate text-xs text-muted-foreground">
                {previewClip.assetName}
                {previewClip.inTime != null && previewClip.outTime != null && (
                  <> — {formatTime(previewClip.inTime)}s to {formatTime(previewClip.outTime)}s</>
                )}
              </span>
              <button
                onClick={() => { setPreviewClipIndex(null); setIsVideoPlaying(false); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Conversation */}
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-3">
          {/* Script Upload Prompt */}
          {!scriptContent && conversation.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-border p-4 text-center">
              <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
              <p className="mb-2 text-xs text-muted-foreground">
                Have a script? Upload it for better results
              </p>
              <input
                ref={initialFileInputRef}
                type="file"
                accept=".txt,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => initialFileInputRef.current?.click()}
                className="border-border text-xs"
              >
                <Upload className="mr-1 h-3 w-3" />
                Upload Script
              </Button>
              <p className="mt-2 text-[10px] text-muted-foreground/70">
                Or just describe what you want below
              </p>
            </div>
          )}

          {/* Messages */}
          {conversation.map((msg, i) => (
            <div
              key={i}
              className={`rounded-lg border p-3 text-xs ${
                msg.role === 'ai'
                  ? 'border-border bg-secondary/30'
                  : 'ml-4 border-primary/30 bg-primary/5'
              }`}
            >
              <div className="mb-1 flex items-center gap-1">
                {msg.role === 'ai' ? (
                  <Sparkles className="h-3 w-3 text-accent" />
                ) : (
                  <MessageSquare className="h-3 w-3 text-primary" />
                )}
                <span className="font-medium">
                  {msg.role === 'ai' ? 'AI Editor' : 'You'}
                </span>
                <span className="ml-auto text-[9px] text-muted-foreground">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-muted-foreground">{msg.content}</p>
            </div>
          ))}

          {/* Processing */}
          {isProcessing && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 p-3">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-xs text-muted-foreground">AI is thinking...</span>
            </div>
          )}

          {/* Quick Questions — multi-select toggle chips */}
          {questions.length > 0 && !isProcessing && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-medium text-muted-foreground">Quick responses:</p>
              {questions.map((q) => {
                const selected = selectedOptions[q.id] || new Set<string>();
                const hasSelection = selected.size > 0;
                return (
                  <div key={q.id} className="space-y-1">
                    <p className="text-xs font-medium">{q.question}</p>
                    <div className="flex flex-wrap gap-1">
                      {q.options.map((option, optIdx) => {
                        const isSelected = selected.has(option);
                        const isSkipOption = /^(none|no|skip|no thanks)$/i.test(option.trim());
                        return (
                          <Button
                            key={optIdx}
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            className={`h-6 px-2 text-[10px] ${isSelected ? 'bg-primary text-primary-foreground' : 'border-border'}`}
                            onClick={() => {
                              // "None" / "Skip" style options send immediately
                              if (isSkipOption) {
                                setSelectedOptions(prev => ({ ...prev, [q.id]: new Set() }));
                                onQuestionSelect(q.id, option);
                                return;
                              }
                              const newSelected = new Set(selected);
                              if (isSelected) {
                                newSelected.delete(option);
                              } else {
                                newSelected.add(option);
                              }
                              // Deselect any skip-type options when selecting a real option
                              for (const o of newSelected) {
                                if (/^(none|no|skip|no thanks)$/i.test(o.trim())) {
                                  newSelected.delete(o);
                                }
                              }
                              setSelectedOptions(prev => ({ ...prev, [q.id]: newSelected }));
                            }}
                            disabled={isProcessing}
                          >
                            {isSelected && <Check className="mr-1 h-3 w-3" />}
                            {option}
                          </Button>
                        );
                      })}
                    </div>
                    {/* Send selected options or pick single */}
                    {hasSelection && (
                      <Button
                        size="sm"
                        className="mt-1 h-6 px-3 text-[10px]"
                        onClick={() => {
                          const choices = Array.from(selected).join(', ');
                          setSelectedOptions(prev => ({ ...prev, [q.id]: new Set() }));
                          onQuestionSelect(q.id, choices);
                        }}
                        disabled={isProcessing}
                      >
                        <Send className="mr-1 h-3 w-3" />
                        Send ({selected.size} selected)
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Edit Plan Summary */}
          {editPlan && (
            <div className="mt-4 rounded-lg border border-success/30 bg-success/5 p-4">
              <div className="mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <h4 className="text-xs font-semibold">Your Edit Plan</h4>
              </div>

              <p className="mb-4 text-xs text-muted-foreground">{editPlan.summary}</p>

              {/* Plan Details */}
              <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <Palette className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Tone:</span>
                  <Badge variant="secondary" className="text-[9px]">{editPlan.tone}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Video className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Style:</span>
                  <Badge variant="secondary" className="text-[9px]">{editPlan.style}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {getPacingIcon(editPlan.pacing)}
                  <span className="text-muted-foreground">Pacing:</span>
                  <Badge variant="secondary" className="text-[9px]">{editPlan.pacing}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Duration:</span>
                  <Badge variant="secondary" className="text-[9px]">~{editPlan.suggestedDuration}s</Badge>
                </div>
              </div>

              {/* Key Moments */}
              {editPlan.keyMoments && editPlan.keyMoments.length > 0 && (
                <div className="mb-4">
                  <div className="mb-2 flex items-center gap-1">
                    <Target className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium">Key Moments</span>
                  </div>
                  <div className="space-y-1">
                    {editPlan.keyMoments.map((moment, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span>{i + 1}.</span>
                        <span>{moment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Music Direction */}
              {editPlan.musicDirection && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center gap-1">
                    <Music className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium">Music</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{editPlan.musicDirection}</p>
                </div>
              )}

              {/* Transition Style */}
              {editPlan.transitionStyle && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center gap-1">
                    <Scissors className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium">Transitions</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{editPlan.transitionStyle}</p>
                </div>
              )}

              {/* Text Overlays */}
              {editPlan.textOverlays && editPlan.textOverlays.length > 0 && (
                <div className="mb-4">
                  <div className="mb-2 flex items-center gap-1">
                    <FileText className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium">Text Overlays</span>
                  </div>
                  <div className="space-y-1">
                    {editPlan.textOverlays.map((text, i) => (
                      <div key={i} className="rounded bg-secondary/50 px-2 py-1 text-xs text-muted-foreground">
                        "{text}"
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clip Priorities */}
              {editPlan.clipPriorities && editPlan.clipPriorities.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Video className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs font-medium">Clip Order</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground">Drag to reorder</span>
                  </div>

                  <div className="space-y-1">
                    {modifiedClips.map((clip, i) => {
                      const hasVideoUrl = !!getClipVideoUrl(clip);
                      const clipDuration = clip.inTime != null && clip.outTime != null
                        ? (clip.outTime - clip.inTime).toFixed(1)
                        : null;
                      return (
                        <div
                          key={`${clip.assetName}-${i}`}
                          draggable
                          onDragStart={() => handleDragStart(i)}
                          onDragOver={(e) => handleDragOver(e, i)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 cursor-move transition-opacity text-xs ${
                            draggedIndex === i ? 'opacity-50' : 'opacity-100'
                          } ${previewClipIndex === i ? 'bg-primary/10 border border-primary/30' : 'hover:bg-secondary/30 border border-transparent'}`}
                        >
                          <GripVertical className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
                          <span className={`flex-shrink-0 w-2 h-2 rounded-full ${
                            clip.priority === 'primary' ? 'bg-primary' : clip.priority === 'secondary' ? 'bg-accent' : 'bg-muted-foreground/30'
                          }`} />
                          <span className="min-w-0 flex-1 truncate text-foreground/80">{clip.assetName}</span>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={clip.inTime != null ? clip.inTime : ''}
                            onChange={(e) => handleTrimChange(i, 'inTime', e.target.value)}
                            className="h-6 w-14 flex-shrink-0 rounded border border-border/50 bg-input px-1.5 text-xs text-center text-foreground tabular-nums"
                            placeholder="in"
                            title="In point (seconds)"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="text-muted-foreground/50">–</span>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={clip.outTime != null ? clip.outTime : ''}
                            onChange={(e) => handleTrimChange(i, 'outTime', e.target.value)}
                            className="h-6 w-14 flex-shrink-0 rounded border border-border/50 bg-input px-1.5 text-xs text-center text-foreground tabular-nums"
                            placeholder="out"
                            title="Out point (seconds)"
                            onClick={(e) => e.stopPropagation()}
                          />
                          {clipDuration && (
                            <span className="flex-shrink-0 text-[11px] text-muted-foreground/60 w-10 text-right">{clipDuration}s</span>
                          )}
                          {hasVideoUrl && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePreviewClip(i); }}
                              className={`flex-shrink-0 p-1 rounded transition-colors ${
                                previewClipIndex === i ? 'text-primary bg-primary/10' : 'text-muted-foreground/50 hover:text-primary hover:bg-primary/10'
                              }`}
                              title="Preview clip"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveClip(i)}
                            className="flex-shrink-0 p-1 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Remove clip"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Finishing Touches */}
              <div className="mt-4 rounded-lg border border-border bg-secondary/20 p-3">
                <div className="mb-3 flex items-center gap-2">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold">Finishing Touches</span>
                </div>
                <div className="space-y-2.5">
                  {/* Audio Enhancement */}
                  <label className="flex cursor-pointer items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Volume2 className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Enhance Audio</span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={finishingTouches.enhanceAudio || false}
                      onClick={() => onFinishingTouchesChange({ ...finishingTouches, enhanceAudio: !finishingTouches.enhanceAudio })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        finishingTouches.enhanceAudio ? 'bg-primary' : 'bg-secondary'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          finishingTouches.enhanceAudio ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`}
                      />
                    </button>
                  </label>

                  {/* Captions */}
                  <div>
                    <label className="flex cursor-pointer items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Captions className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Add Captions</span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={finishingTouches.addCaptions || false}
                        onClick={() => onFinishingTouchesChange({
                          ...finishingTouches,
                          addCaptions: !finishingTouches.addCaptions,
                          captionStyle: !finishingTouches.addCaptions ? 'bottom-center' : undefined,
                        })}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          finishingTouches.addCaptions ? 'bg-primary' : 'bg-secondary'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            finishingTouches.addCaptions ? 'translate-x-[18px]' : 'translate-x-[3px]'
                          }`}
                        />
                      </button>
                    </label>
                    {finishingTouches.addCaptions && (
                      <div className="ml-5 mt-1.5 flex gap-1">
                        {CAPTION_STYLE_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => onFinishingTouchesChange({ ...finishingTouches, captionStyle: opt.value as any })}
                            className={`rounded px-2 py-0.5 text-[9px] transition-colors ${
                              finishingTouches.captionStyle === opt.value
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Visual Filter */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Filter</span>
                    </div>
                    <select
                      value={finishingTouches.filter || ''}
                      onChange={(e) => onFinishingTouchesChange({ ...finishingTouches, filter: e.target.value || undefined })}
                      className="h-6 rounded border border-border bg-secondary px-2 text-[10px] text-foreground"
                    >
                      {FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-border p-3">
        {!isPlanComplete ? (
          <div className="space-y-2">
            <Textarea
              placeholder="Describe your video...

Example: Create a 45-second highlight reel showing each person's name and their most interesting fact. Use dissolve transitions and add name titles at the bottom."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="min-h-[80px] resize-none border-border bg-input text-xs placeholder:text-muted-foreground/50 focus:ring-primary"
              disabled={isProcessing}
            />
            <div className="flex gap-2">
              {onScriptUpload && !scriptContent && (
                <>
                  <input
                    ref={inlineFileInputRef}
                    type="file"
                    accept=".txt,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => inlineFileInputRef.current?.click()}
                    className="border-border text-xs"
                  >
                    <Upload className="mr-1 h-3 w-3" />
                    Script
                  </Button>
                </>
              )}
              <Button
                size="sm"
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleSend}
                disabled={!inputValue.trim() || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Format:</span>
              <Select value={aspectRatio} onValueChange={onAspectRatioChange}>
                <SelectTrigger className="h-8 flex-1 border-border bg-secondary/50 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover">
                  <SelectItem value="source" className="text-xs">Match Source</SelectItem>
                  <SelectItem value="16:9" className="text-xs">16:9 (Landscape)</SelectItem>
                  <SelectItem value="9:16" className="text-xs">9:16 (Portrait/TikTok)</SelectItem>
                  <SelectItem value="1:1" className="text-xs">1:1 (Square)</SelectItem>
                  <SelectItem value="4:5" className="text-xs">4:5 (Instagram)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="h-12 w-full bg-primary text-base text-primary-foreground hover:bg-primary/90"
              onClick={() => onApproveAndRender(modifiedClips)}
              disabled={isProcessing || isRendering || modifiedClips.length === 0}
            >
              {isRendering ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Rendering...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-5 w-5" />
                  Approve & Render ({modifiedClips.length} clips)
                </>
              )}
            </Button>
            {!isRendering && (
              <>
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Describe changes... e.g. cut between people instead of picture-in-picture"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    className="min-h-[40px] resize-none border-border bg-input text-xs placeholder:text-muted-foreground/50 focus:ring-primary"
                    disabled={isProcessing}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-border"
                    onClick={onRefinePlan}
                    disabled={isProcessing}
                  >
                    <MessageSquare className="mr-1 h-3 w-3" />
                    Refine Plan
                  </Button>
                  {inputValue.trim() && (
                    <Button
                      size="sm"
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={handleSend}
                      disabled={isProcessing}
                    >
                      <Send className="mr-1 h-3 w-3" />
                      Send
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {!isPlanComplete && (
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            Tell me what kind of video you want to create
          </p>
        )}
      </div>
    </Card>
  );
};
